import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ResourceCacheService } from './resource-cache.service';
import { StockaidResponse } from '../misc/stockaid-response';
import { Pagination } from 'src/app/core/infrastructure/classes/pagination';
import { SortAttribute } from 'src/app/core/infrastructure/classes/sort-attribute';
import { SortDescriptor } from '@progress/kendo-data-query';
import { v4 as uuid } from 'uuid';
import { stringifyJSONWithDates } from 'src/app/core/utils';
import {
  IAdditionalKeys,
  AdditionalKeysType,
} from 'src/app/core/infrastructure/interfaces/additional-keys.interface';
import { GridName } from 'src/app/theme/shared/forecastui/forecast-rxdata-table/forcast-rxdata-table.constant';
import _ from 'lodash';
import { GlobalFilters } from '../models/user';

export abstract class ResourceService<T> {
  private countCache$: Observable<number> | null = null;
  private countFilter: any;
  private countItemsKeySelected: string[] = [];
  private countTime = 0;
  private countLastSyncDate: Date | null = null;
  protected currentCompanyKey: string;

  private readonly LOCAL_COUNT_PERSIST_THRESHOLD = 1000000;
  private readonly COUNT_CACHE_EXPIRATION_MS = 1000 * 60;

  constructor(
    protected httpClient: HttpClient,
    protected apiUrl: string,
    protected cacheService?: ResourceCacheService
  ) {}

  getStockAid(): Observable<StockaidResponse<T[]>> {
    return this.httpClient.get<StockaidResponse<T[]>>(this.apiUrl);
  }

  getAll(): Observable<T[]> {
    return this.httpClient.get<T[]>(this.apiUrl);
  }

  getFiltered(
    pagination: Pagination,
    sort: SortAttribute[],
    filter: any,
    altApiUrl: string,
    itemsKeySelected: string[],
    additionalOptions: {
      restockKey?: string;
      marketplaceId?: string;
      currencyCode?: string;
      demandSourcePreference?: string;
      isAWD?: boolean;
      purchaseOrderKey?: string;
      unpaged?: boolean;
      entityType?: string;
      entityKey?: string;
      globalFilters?: GlobalFilters;
      type?: string;
      skus?: string;
    }
  ): Observable<T[]> {
    const {
      restockKey,
      marketplaceId,
      currencyCode,
      demandSourcePreference,
      isAWD,
      purchaseOrderKey,
      unpaged,
      entityKey,
      entityType,
      globalFilters,
      type,
      skus
    } = additionalOptions || {};
    pagination = pagination || null;
    sort = sort || [];
    filter = filter || null;

    let params = new HttpParams();

    if (sort.length) {
      params = params.set('sort', JSON.stringify(sort));
    }

    if (entityType) {
      filter.filters.push(
        {
          filters: [{ field: 'entityType', operator: 'eq', value: entityType }],
          logic: 'and',
        },
      )
    }

    if (entityKey) {
      filter.filters.push(
        {
          filters: [{ field: 'entityKey', operator: 'eq', value: entityKey }],
          logic: 'and',
        }
      )
    }

    if (type) {
      filter.filters.push(
        {
          filters: [{ field: 'type', operator: 'eq', value: type }],
          logic: 'and',
        }
      )
    }

    if (filter) {
      params = params.set('where', stringifyJSONWithDates(filter));
    }

    if (itemsKeySelected?.length) {
      const getRequests = this.getMultiple(
        itemsKeySelected,
        this.apiUrl,
        params
      );

      return forkJoin(getRequests).pipe(
        map((results) => {
          let items = [];
          results.forEach((rs) => (items = items.concat(rs)));

          return items as T[];
        })
      );
    }

    if (pagination) {
      params = params.set('offset', pagination.offset?.toString());
      params = params.set('limit', pagination.limit?.toString());
    }

    if (restockKey) {
      params = params.set('restockKey', restockKey);
    }

    if (isAWD) {
      params = params.set('isAWD', isAWD.toString());
    }

    if (purchaseOrderKey) {
      params = params.set('purchaseOrderKey', purchaseOrderKey);
    }

    if (currencyCode) {
      params = params
        .set('marketplaceId', marketplaceId)
        .set('currencyCode', currencyCode);
    }

    if (demandSourcePreference) {
      params = params.set('demandSourcePreference', demandSourcePreference);
    }

    if(unpaged){
      params = params.set('unpaged', String(unpaged));
    }

    if(globalFilters){
      params = params.set('globalFilters', JSON.stringify(globalFilters));
    }

    if (skus) {
      params = params.set('skus', skus);
    }

    return this.httpClient.get<T[]>(altApiUrl || `${this.apiUrl}`, { params });
  }

  getFilteredUserTelerikEvent(
    skip: number,
    take: number,
    filter: any,
    sort: SortDescriptor[] = [],
    altApiUrl = '',
    itemsKeySelected?: string[],
    additionalOptions?: {
      restockKey?: string;
      marketplaceId?: string;
      currencyCode?: string;
      demandSourcePreference?: string;
      isAWD?: boolean;
      purchaseOrderKey?: string;
      unpaged?: boolean;
      entityType?: string;
      entityKey?: string;
      type?: string;
      globalFilters?: GlobalFilters;
      skus?: string;
    }
  ): Observable<T[]> {
    const {
      restockKey,
      marketplaceId,
      currencyCode,
      demandSourcePreference,
      isAWD,
      purchaseOrderKey,
      unpaged,
      entityKey,
      entityType,
      globalFilters,
      type,
      skus
    } = additionalOptions || {};
    const pagination = new Pagination({ offset: skip, limit: take });

    const sortArray = this.parseSortT(sort);

    return this.getFiltered(
      pagination,
      sortArray,
      filter,
      altApiUrl,
      itemsKeySelected,
      {
        restockKey,
        marketplaceId,
        currencyCode,
        demandSourcePreference,
        isAWD,
        purchaseOrderKey,
        unpaged,
        entityKey,
        entityType,
        globalFilters,
        type,
        skus
      }
    );
  }

  protected parseSortT(sort: SortDescriptor[]): SortAttribute[] {
    //if undefined, return empty array.
    if (!sort.length) {
      return [];
    }

    return sort.map((sorter) => {
      return new SortAttribute({
        field: sorter.field,
        direction: sorter.dir || 'asc',
      });
    });
  }

  getCount(
    filter: any = null,
    altApiUrl = '',
    companyKey: string = null,
    itemsKeySelected: string[] = [],
    additionalOptions?: {
      marketplaceId?: string;
      currencyCode?: string;
      demandSourcePreference?: string;
      isAWD?: boolean;
      isRefreshed?: boolean;
      entityType?: string;
      entityKey?: string;
      globalFilters?: GlobalFilters;
      type?: string;
      lastSyncDate?: Date;
      skus?: string;
    }
  ): Observable<number> {
    const {
      marketplaceId,
      currencyCode,
      demandSourcePreference,
      isAWD,
      isRefreshed,
      entityType,
      entityKey,
      globalFilters,
      type,
      lastSyncDate,
      skus,
    } = additionalOptions || {};

    let params = new HttpParams();

    if (entityType) {
      filter.filters.push(
        {
          filters: [{ field: 'entityType', operator: 'eq', value: entityType }],
          logic: 'and',
        },
      )
    }

    if (entityKey) {
      filter.filters.push(
        {
          filters: [{ field: 'entityKey', operator: 'eq', value: entityKey }],
          logic: 'and',
        }
      )
    }

    if (type) {
      filter.filters.push(
        {
          filters: [{ field: 'type', operator: 'eq', value: type }],
          logic: 'and',
        }
      )
    }

    if (globalFilters) {
      params = params.set('globalFilters', JSON.stringify(globalFilters));
    }

    if (skus) {
      params = params.set('skus', skus);
    }

    if (filter) {
      params = params.set('where', stringifyJSONWithDates(filter));
    }

    if (itemsKeySelected.length) {
      const getRequests = this.getMultiple(
        itemsKeySelected,
        this.apiUrl,
        params
      );

      return forkJoin(getRequests).pipe(
        map((results) => {
          let count: number = 0;
          results.forEach((rs) => (count += rs.length));
          return count;
        })
      );
    }

    if (currencyCode) {
      params = params
        .set('marketplaceId', marketplaceId)
        .set('currencyCode', currencyCode);
    }

    if (demandSourcePreference) {
      params = params.set('demandSourcePreference', demandSourcePreference);
    }

    if (isAWD) {
      params = params.set('isAWD', isAWD.toString());
      return this.httpClient.get<number>(`${altApiUrl || this.apiUrl}/count`, {
        params,
      });
    }

    if (this.cacheService) {
      return this.resolveCountWithCacheService(
        params, filter, companyKey, itemsKeySelected, altApiUrl, lastSyncDate, isRefreshed
      );
    }

    const currentTime = new Date().getTime();

    if (
      !this.countCache$ ||
      currentTime - this.countTime > this.COUNT_CACHE_EXPIRATION_MS ||
      !_.isEqual(filter, this.countFilter) ||
      !_.isEqual(itemsKeySelected, this.countItemsKeySelected) ||
      (companyKey && companyKey !== this.currentCompanyKey) ||
      isRefreshed
    ) {
      this.countCache$ = this.httpClient
        .get<number>(`${altApiUrl || this.apiUrl}/count`, {
          params,
        })
        .pipe(shareReplay(1));

      this.countTime = currentTime;
      this.countFilter = filter;
      this.countItemsKeySelected = itemsKeySelected;
      this.currentCompanyKey = companyKey;
    }

    return this.countCache$;
  }

  // The api for getting count is 'api/count/[demand, supply, bom]'
  getCountDSB(filter: any = null): Observable<number> {
    let params = new HttpParams();

    if (filter) {
      params = params.set('where', stringifyJSONWithDates(filter));
    }
    const url = this.apiUrl.split('/');

    return this.httpClient.get<number>(`api/count/${url[2]}`, { params });
  }

  getById(
    id: any,
    marketplaceId?: string | boolean,
    currencyCode?: string
  ): Observable<T> {
    let params = new HttpParams();
    if (currencyCode) {
      params = params
        .set('marketplaceId', marketplaceId as string)
        .set('currencyCode', currencyCode);
    }

    return this.httpClient.get<T>(`${this.apiUrl}/${encodeURIComponent(id)}`, {
      params,
    });
  }

  save(
    item: T | Partial<T>,
    objectKey: any = 'id',
    additionalKeys: IAdditionalKeys = {},
    additionalOptions: any = {}
  ): Observable<any> {
    // Only Demand, Supply, Bom will have additionalKeys.type
    if (!additionalKeys.type && (item[objectKey] === '' || !item[objectKey])) {
      // new item
      return this.withCacheInvalidation(this.httpClient.post(`${this.apiUrl}`, item));
    }
    let params = new HttpParams();

    const setParams = (key: string, value: string) => {
      if (value) {
        params = params.set(key, value)
      }
    }

    switch (additionalKeys.type) {
      case AdditionalKeysType.createBom:
        return this.withCacheInvalidation(this.httpClient.post(
          `${this.apiUrl}/${encodeURIComponent(
            item['parentKey']
          )}/${encodeURIComponent(item['childKey'])}`,
          item
        ));

      case AdditionalKeysType.updateBom:
        if (additionalKeys.parentKey && additionalKeys.childKey) {
          // When updating parent name or child name of a bom row
          // Need to preserve the old parentKey and childKey
          return this.withCacheInvalidation(this.httpClient.put(
            `${this.apiUrl}/${encodeURIComponent(
              additionalKeys.parentKey
            )}/${encodeURIComponent(additionalKeys.childKey)}`,
            item
          ));
        }

        // When simply updating quantity of a bom row or through adding a bom (same parent name, child name)
        return this.withCacheInvalidation(this.httpClient.put(
          `${this.apiUrl}/${encodeURIComponent(
            item['parentKey']
          )}/${encodeURIComponent(item['childKey'])}`,
          item
        ));

      case AdditionalKeysType.demand:
      case AdditionalKeysType.supply: {
        if (item['docType'] && item['orderKey'] && item['rowKey']) {
          let query = `${encodeURIComponent(
            item['docType']
          )}/${encodeURIComponent(item['orderKey'])}/${encodeURIComponent(
            item['rowKey']
          )}`;
          if (additionalKeys.type === AdditionalKeysType.demand) {
            query += `/${encodeURIComponent(item['orderItemId'])}`;
          }

          return this.withCacheInvalidation(this.httpClient.put(`${this.apiUrl}/${query}`, item));
        }

        setParams('marketplaceId', additionalOptions?.marketplaceId)
        setParams('currencyCode', additionalOptions?.currencyCode)
        setParams('isClosePo', additionalOptions?.isClosePo)

        item['docType'] = 'import';
        item['orderKey'] = uuid();
        item['rowKey'] = uuid();

        let query = `${encodeURIComponent(
          item['docType']
        )}/${encodeURIComponent(item['orderKey'])}/${encodeURIComponent(
          item['rowKey']
        )}`;
        if (additionalKeys.type === AdditionalKeysType.demand) {
          item['orderItemId'] = 'null';
          query += `/${encodeURIComponent(item['orderItemId'])}`;
        }

        return this.withCacheInvalidation(this.httpClient.post(
          `${this.apiUrl}/${query}`,
          item,
          { params }
        ));
      }

      case AdditionalKeysType.itemSite:
        return this.withCacheInvalidation(this.httpClient.put(
          `${this.apiUrl}/${encodeURIComponent(
            item['itemKey']
          )}/${encodeURIComponent(item['key'])}`,
          item
        ));

      default:
        setParams('shipmentItemKey', additionalOptions?.shipmentItemKey);
        setParams('marketplaceId', additionalOptions?.marketplaceId);
        setParams('currencyCode', additionalOptions?.currencyCode);
        setParams('isClosePo', additionalOptions?.isClosePo);

        return this.withCacheInvalidation(this.httpClient.put(
          `${this.apiUrl}/${encodeURIComponent(item[objectKey])}`,
          item,
          { params }
        ));
    }
  }

  delete(id: any): Observable<any> {
    return this.withCacheInvalidation(this.httpClient.delete(`${this.apiUrl}/${id}`));
  }

  deleteHideMultiple(
    ids: string[],
    itemName?: string,
    isHiding = false
  ): Observable<any> {
    let finalIds: string[];
    if ([GridName.Bom, GridName.ItemSite].includes(itemName as GridName)) {
      // Filter out ids of bom that already included in the parent keys
      const parentKeys = [];
      ids.forEach((id) => id.split('/').length === 1 && parentKeys.push(id));
      finalIds = ids.filter((id) => {
        const [parentKey, childKey] = id.split('/') || [];
        return !childKey || !parentKeys.includes(parentKey);
      });
    } else {
      finalIds = ids;
    }

    const idsChunks: string[][] = [];
    while (finalIds?.length) {
      idsChunks.push(finalIds.splice(0, 5000));
    }

    const updateRequests: Observable<any>[] = [];
    idsChunks.forEach((idsToBeUpdated) => {
      const httpOptions = {
        body: {
          ids: idsToBeUpdated,
          itemName
        },
      } as any;
      updateRequests.push(
        isHiding
          ? this.httpClient.post(`${this.apiUrl}/hide`, { ids: idsToBeUpdated })
          : this.httpClient.delete(`${this.apiUrl}`, httpOptions)
      );
    });

    let isFinalPODeleted = false;
    return this.withCacheInvalidation(forkJoin(updateRequests).pipe(
      map((results) => {
        let failToUpdateItems = [];
        results.forEach(({ failItems, isPODeleted }) => {
          isFinalPODeleted = isPODeleted || isFinalPODeleted;
          failToUpdateItems = failToUpdateItems.concat(failItems);
        });

        return {
          failToUpdateItems: failToUpdateItems.filter((i) => i),
          isPODeleted: isFinalPODeleted,
        };
      })
    ));
  }

  getByMultipleIds(ids: string[]): Observable<T[]> {
    if (!ids.length) {
      return of([]);
    }

    const idsChunks: string[][] = [];
    while (ids?.length) {
      idsChunks.push(ids.splice(0, 100));
    }

    const getRequests: Observable<any>[] = [];
    idsChunks.forEach((chunk) => {
      let includedIds = '';
      chunk.forEach(
        (id, idx) =>
        (includedIds +=
          idx === chunk.length - 1
            ? encodeURIComponent(id)
            : `${encodeURIComponent(id)},`)
      );

      getRequests.push(
        this.httpClient.get<T[]>(`${this.apiUrl}?ids=${includedIds}`)
      );
    });

    return forkJoin(getRequests).pipe(
      map((results) => {
        let items = [];
        results.forEach((rs) => (items = items.concat(rs)));

        return items;
      })
    );
  }

  getMultiple(idsOrigin, url, params): Observable<any>[] {
    const idsChunks: string[][] = [];
    const ids = [...idsOrigin];
    while (ids?.length) {
      idsChunks.push(ids.splice(0, 100));
    }

    const getRequests: Observable<any>[] = [];
    idsChunks.forEach((chunk) => {
      let includedIds = '';
      chunk.forEach(
        (id, idx) =>
        (includedIds +=
          idx === chunk.length - 1
            ? encodeURIComponent(id)
            : `${encodeURIComponent(id)},`)
      );

      const newParams = params.set('ids', includedIds);

      getRequests.push(this.httpClient.get(url, { params: newParams }));
    });

    return getRequests;
  }

  get serviceApiUrl() {
    return this.apiUrl;
  }

  updateMultiple(dataItems: T[]): Observable<any> {
    return this.httpClient.put<any>(`${this.apiUrl}`, dataItems);
  }

  
  /**
   * Orchestrates count resolution when a ResourceCacheService is available.
   *
   * Decision flow:
   *   1. Build a stable cache key (sync-date-based or fallback).
   *   2. Return the cached value immediately if nothing has changed.
   *   3. Evict stale entries when the sync date advances.
   *   4. Serve from localStorage before hitting the network (sync-date mode only).
   *   5. Fetch from HTTP and persist the result.
   */
  private resolveCountWithCacheService(
    params: HttpParams,
    filter: any,
    companyKey: string,
    itemsKeySelected: string[],
    altApiUrl: string,
    lastSyncDate: Date | undefined,
    isRefreshed: boolean | undefined
  ): Observable<number> {
    const cache = this.cacheService!;
    const currentTime = new Date().getTime();
    const isValidSyncDate = this.isSyncDateValid(lastSyncDate);
    const syncDateChanged = isValidSyncDate && this.hasSyncDateChanged(lastSyncDate!);

    const countKey = this.buildCountCacheKey(cache, companyKey, lastSyncDate, filter, isValidSyncDate);
    const isExpired = this.isCacheExpired(isValidSyncDate, syncDateChanged, currentTime);
    const isStateChanged = this.isCacheStateChanged(cache, countKey, isExpired, filter, itemsKeySelected, companyKey, isRefreshed);

    // Cache hit: nothing has changed — return the in-memory value as-is.
    if (!isStateChanged) {
      return cache.get<number>(countKey)!;
    }

    // The sync date advanced: remove all cache entries tied to the previous date.
    if (syncDateChanged) {
      cache.evictStaleByApiUrl(this.apiUrl, companyKey, lastSyncDate!);
    }

    this.updateCountTrackingFields(currentTime, lastSyncDate, filter, itemsKeySelected, companyKey);

    // In sync-date mode (and not a forced refresh), prefer the persisted
    // localStorage value before making a network request.
    const localCount = isValidSyncDate && !isRefreshed ? cache.getLocalCount(countKey) : null;
    if (localCount !== null) {
      return cache.set<number>(countKey, of(localCount));
    }

    return this.fetchAndCacheCount(cache, countKey, altApiUrl, params, isValidSyncDate);
  }

  /**
   * Returns true only when `date` is a real, non-NaN Date instance.
   * Guards against undefined / invalid dates being used as cache keys.
   */
  private isSyncDateValid(date: Date | undefined): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Returns true when the incoming sync date differs from the last recorded one.
   * A change means the data set has been refreshed upstream and the old cache is stale.
   */
  private hasSyncDateChanged(lastSyncDate: Date): boolean {
    return lastSyncDate.getTime() !== this.countLastSyncDate?.getTime();
  }

  /**
   * Builds the cache key for the count value.
   *
   * - Sync-date mode: a stable, content-addressed key that survives page reloads
   *   (stored in localStorage by ResourceCacheService).
   * - Fallback mode: a simple URL-based key valid only for the current session.
   */
  private buildCountCacheKey(
    cache: ResourceCacheService,
    companyKey: string,
    lastSyncDate: Date | undefined,
    filter: any,
    isValidSyncDate: boolean
  ): string {
    return isValidSyncDate
      ? cache.buildCountKey(this.apiUrl, companyKey, lastSyncDate!, filter)
      : `${this.apiUrl}:count`;
  }

  /**
   * Determines whether the cached count has expired.
   *
   * - Sync-date mode: expired only when the sync date itself changes (data was re-synced).
   * - Fallback mode: expired after COUNT_CACHE_EXPIRATION_MS milliseconds.
   */
  private isCacheExpired(isValidSyncDate: boolean, syncDateChanged: boolean, currentTime: number): boolean {
    return isValidSyncDate
      ? syncDateChanged
      : currentTime - this.countTime > this.COUNT_CACHE_EXPIRATION_MS;
  }

  /**
   * Returns true when any input that affects the count has changed, requiring
   * a fresh value to be fetched (filter, company, selection, forced refresh, or expiry).
   */
  private isCacheStateChanged(
    cache: ResourceCacheService,
    countKey: string,
    isExpired: boolean,
    filter: any,
    itemsKeySelected: string[],
    companyKey: string,
    isRefreshed: boolean | undefined
  ): boolean {
    const hasCached = !!cache.get<number>(countKey);

    return (
      !hasCached ||
      isExpired ||
      !_.isEqual(filter, this.countFilter) ||
      !_.isEqual(itemsKeySelected, this.countItemsKeySelected) ||
      (!!companyKey && companyKey !== this.currentCompanyKey) ||
      !!isRefreshed
    );
  }

  /**
   * Fetches the count from the API and stores it in the cache.
   *
   * In sync-date mode the result is also persisted to localStorage
   * (up to LOCAL_COUNT_PERSIST_THRESHOLD items) so it survives a page reload.
   */
  private fetchAndCacheCount(
    cache: ResourceCacheService,
    countKey: string,
    altApiUrl: string,
    params: HttpParams,
    isValidSyncDate: boolean
  ): Observable<number> {
    const countUrl = `${altApiUrl || this.apiUrl}/count`;
    const httpObs$ = this.httpClient.get<number>(countUrl, { params });

    return isValidSyncDate
      ? cache.set<number>(countKey, httpObs$, this.LOCAL_COUNT_PERSIST_THRESHOLD)
      : cache.set<number>(countKey, httpObs$);
  }

  private updateCountTrackingFields(
    time: number,
    syncDate: Date | undefined,
    filter: any,
    itemsKeySelected: string[],
    companyKey: string
  ): void {
    this.countTime = time;
    this.countLastSyncDate = syncDate ?? null;
    this.countFilter = filter;
    this.countItemsKeySelected = itemsKeySelected;
    this.currentCompanyKey = companyKey;
  }

  protected invalidateCaches(): void {
    this.countCache$ = null;
    this.cacheService?.invalidateByApiUrl(this.apiUrl);
  }

  private withCacheInvalidation<T>(source$: Observable<T>): Observable<T> {
    return source$.pipe(tap(() => this.invalidateCaches()));
  }
}
